/** @jsx isolateComponent */
import { PlanFeatureList } from '@zapier/design-system-revenue/src/components/pricing-pages/PricingPageV3/PlanFeatureList';
import { PlanSummary } from '@zapier/design-system-revenue/src/components/pricing-pages/PricingPageV3/PlanSummary';
import { TaskLimitDropdownSubstitute } from '@zapier/design-system-revenue/src/components/pricing-pages/PricingPageV3/TaskLimitDropdownSubstitute';
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/common-utils';
import { Colors, Spacer } from '@zapier/design-system';

import {
  Currency,
  PlanV2,
  Size,
  PlanTier,
} from '@zapier/design-system-revenue/src/types';
import { formatPriceToBeInCurrency } from '@zapier/design-system-revenue/src/utils';

import { PlanActionButtonSubstitute } from './PlanActionButtonSubstitute';

type LegacyPlanCardProps = {
  /** The type of currency to display. */
  currency: Currency;
  /** The plan to display the information of. */
  plan: PlanV2;
  /** The size of the screen. */
  size: Size;
};

const Styles = {
  root: css({
    background: Colors.grey100,
    borderRadius: 5,
    display: 'grid',
    gridTemplateRows: '50px 35px 15px',
    margin: '0 auto',
    padding: 20,
    width: 250,
  }),
};

const getMostExpensiveTier = (tiers: PlanTier[]) => {
  const max = Math.max(...tiers.map(t => t.pricePerTask));
  return tiers.find(t => t.pricePerTask === max);
};

const formatDisplayPrice = (pricePerTask: number, currency: Currency) => {
  return `${currency.symbol}${formatPriceToBeInCurrency(
    pricePerTask,
    currency,
    false,
    {
      maxDecimals: 4,
      useRelativeRounding: false,
    }
  )}`;
};

const formatTiersString = (tiers: PlanTier[], currency: Currency) => {
  return tiers
    .map(({ numTasks, pricePerTask }, i) => {
      const price =
        pricePerTask === 0
          ? 'Included'
          : formatDisplayPrice(pricePerTask, currency);
      return numTasks === -1
        ? `Any Additional: ${price}`
        : `${i === 0 ? 'First' : 'Next'} ${numTasks.toLocaleString(
            'en-US'
          )}: ${price} USD`;
    })
    .join('\n');
};

const getFeatureList = (currency: Currency, plan: PlanV2) => {
  const { tiers } = plan;
  const extraTaskTier =
    plan.tiers.length > 0 ? getMostExpensiveTier(tiers) : null;
  const features = [];

  if (extraTaskTier) {
    features.push({
      href: null,
      name: `Extra tasks at ${formatDisplayPrice(
        extraTaskTier.pricePerTask,
        currency
      )}`,
      tooltip: formatTiersString(tiers, currency),
    });
  }

  if (plan.type) {
    features.push({
      href: null,
      name: `Everything in ${plan.type}`,
      tooltip: null,
    });
  }

  return features;
};

/**
 * A component that displays details about a user's legacy plan.
 */
export const LegacyPlanCard = ({
  currency,
  plan,
  size,
}: LegacyPlanCardProps) => {
  return (
    <div css={Styles.root}>
      <PlanSummary currency={currency} planVariants={[plan]} title="Legacy" />

      <Spacer height={10} />

      <TaskLimitDropdownSubstitute
        color={Colors.white}
        text={`${plan.tasks} Tasks /mo`}
      />

      <Spacer height={10} />
      <PlanActionButtonSubstitute text="Current Plan" />
      <Spacer height={20} />

      <PlanFeatureList
        features={getFeatureList(currency, plan)}
        plan={plan}
        showDivider={false}
        showTooltips={false}
        size={size}
      />
    </div>
  );
};
